import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { AppShell } from "@/components/AppShell";
import { MatchCard, MatchData } from "@/components/MatchCard";
import { phaseLabels, phaseOrder } from "@/lib/utils-app";
import { PageMeta } from "@/components/PageMeta";
import { AdSlot } from "@/components/ads/AdSlot";
import { TeamLogosProvider } from "@/lib/team-logos";

interface TournamentOpt { id: string; name: string; name_cs?: string | null; name_en?: string | null; year: number }

const GlobalMatches = () => {
  const { t, i18n } = useTranslation();
  const [matchesCache, setMatchesCache] = useState<Record<string, MatchData[]>>({});
  const [tournaments, setTournaments] = useState<TournamentOpt[]>([]);
  const [tournamentId, setTournamentId] = useState<string>("");
  const [phaseFilter, setPhaseFilter] = useState<string>("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase
      .from("tournaments")
      .select("id,name,name_cs,name_en,year")
      .eq("is_active", true)
      .order("year", { ascending: false })
      .then(({ data }) => {
        const list = (data as TournamentOpt[]) ?? [];
        setTournaments(list);
        setTournamentId((prev) => prev || list[0]?.id || "");
      });
  }, []);

  // Lazy-load matches only when a tournament tab is selected; cache per tournament
  // so switching back and forth doesn't refetch.
  useEffect(() => {
    if (!tournamentId || matchesCache[tournamentId]) return;
    let cancelled = false;
    setLoading(true);
    supabase
      .from("matches")
      .select("*")
      .eq("tournament_id", tournamentId)
      .order("kickoff_at")
      .then(({ data }) => {
        if (cancelled) return;
        setMatchesCache((prev) => ({ ...prev, [tournamentId]: ((data as any) ?? []) as MatchData[] }));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [tournamentId, matchesCache]);

  const matches = matchesCache[tournamentId] ?? [];

  const tournamentName = (tr: TournamentOpt) =>
    (i18n.language?.startsWith("en") ? tr.name_en : tr.name_cs) || tr.name;

  const byPhase = useMemo(() => {
    const g: Record<string, MatchData[]> = {};
    matches.forEach(m => { (g[m.phase] = g[m.phase] || []).push(m); });
    return g;
  }, [matches]);


  const availablePhases = useMemo(() => phaseOrder.filter((p) => byPhase[p]), [byPhase]);

  // Default filter: latest phase whose match is played today.
  const defaultPhase = useMemo(() => {
    const today = new Date().toDateString();
    const todaysPhases = new Set(
      matches
        .filter((m) => new Date(m.kickoff_at).toDateString() === today)
        .map((m) => m.phase),
    );
    for (let i = phaseOrder.length - 1; i >= 0; i--) {
      if (todaysPhases.has(phaseOrder[i])) return phaseOrder[i];
    }
    return "all";
  }, [matches]);

  const didInitPhase = useRef("");
  useEffect(() => {
    if (didInitPhase.current !== tournamentId && availablePhases.length) {
      didInitPhase.current = tournamentId;
      setPhaseFilter(
        defaultPhase !== "all" && availablePhases.includes(defaultPhase) ? defaultPhase : "all",
      );
      return;
    }
    if (phaseFilter !== "all" && !availablePhases.includes(phaseFilter)) setPhaseFilter("all");
  }, [availablePhases, phaseFilter, defaultPhase, tournamentId]);

  return (
    <TeamLogosProvider enabled={true}>
    <AppShell title={t("nav.matches")}>
      <PageMeta
        title="Zápasy MS — Tipomatic"
        description="Kompletní rozpis zápasů mistrovství světa ve fotbale podle fází turnaje."
        path="/matches"
      />
      <h1 className="text-2xl font-bold mb-4">{t("nav.matches")}</h1>

      {tournaments.length > 1 && (
        <div className="-mx-4 px-4 mb-3 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max pb-1">
            {tournaments.map((tr) => (
              <button
                key={tr.id}
                type="button"
                onClick={() => setTournamentId(tr.id)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold border transition ${
                  tournamentId === tr.id
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-background text-foreground/70 border-border hover:bg-muted"
                }`}
              >
                {tournamentName(tr)}
              </button>
            ))}
          </div>
        </div>
      )}

      {availablePhases.length > 1 && (
        <div className="-mx-4 px-4 mb-4 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 min-w-max pb-1">
            <button
              type="button"
              onClick={() => setPhaseFilter("all")}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition ${
                phaseFilter === "all"
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background text-foreground/70 border-border hover:bg-muted"
              }`}
            >
              {t("common.all", "Vše")}
            </button>
            {availablePhases.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPhaseFilter(p)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border transition ${
                  phaseFilter === p
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-background text-foreground/70 border-border hover:bg-muted"
                }`}
              >
                {p === "group"
                  ? t("phases.groupShort", "Zákl. skupiny")
                  : p === "league_phase"
                    ? t("phases.leaguePhaseShort", "Ligová fáze")
                    : p === "playoff"
                      ? t("phases.playoffShort", "Play-off")
                      : t(`phases.${p}`, phaseLabels[p])}
              </button>

            ))}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {loading && matches.length === 0 && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t("common.loading", "Načítání…")}
          </div>
        )}
        {!loading && matches.length === 0 && tournamentId && (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t("matches.noMatches", "Zatím žádné zápasy.")}
          </div>
        )}
        {availablePhases.filter((p) => phaseFilter === "all" || p === phaseFilter).map(p => (
          <section key={p}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(`phases.${p}`, phaseLabels[p])}</h2>
            <div className="space-y-2">{byPhase[p].map(m => <MatchCard key={m.id} match={m} />)}</div>
          </section>
        ))}
      </div>
      <AdSlot placement="matches-feed" />
    </AppShell>
    </TeamLogosProvider>
  );
};

export default GlobalMatches;
